// swimPhotoLibrary.js — Phase 113 curated swim photo library.
// -----------------------------------------------------------------------
// Professionally-shot, license-free swim photos (Pexels / Unsplash CDNs)
// grouped by category for the Website Builder photo picker. Every URL was
// verified live before shipping. Coaches can also upload their own photos
// via the existing ImageUpload flow — this library is the “instant
// professional look” starting point.

export const PHOTO_CATEGORIES = [
    { id: 'hero', label: 'Hero shots' },
    { id: 'kids', label: 'Kids & lessons' },
    { id: 'technique', label: 'Technique' },
    { id: 'pool', label: 'Pool & water' },
];

const px = (path) => `https://images.pexels.com/photos/${path}?auto=compress&cs=tinysrgb&w=1600`;
const pxThumb = (path) => `https://images.pexels.com/photos/${path}?auto=compress&cs=tinysrgb&w=480`;

export const SWIM_PHOTOS = [
    // ---- Hero shots -----------------------------------------------------
    {
        id: 'hero-underwater-race',
        category: 'hero',
        label: 'Swimmers mid-race',
        url: px('56837/swimmers-swimming-race-competition-56837.jpeg'),
        thumb: pxThumb('56837/swimmers-swimming-race-competition-56837.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'hero-pool-lanes',
        category: 'hero',
        label: 'Lap lanes from above',
        url: px('261185/pexels-photo-261185.jpeg'),
        thumb: pxThumb('261185/pexels-photo-261185.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'hero-freestyle',
        category: 'hero',
        label: 'Freestyle swimmer',
        url: px('73760/swimming-swimmer-female-race-73760.jpeg'),
        thumb: pxThumb('73760/swimming-swimmer-female-race-73760.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'hero-lesson-wide',
        category: 'hero',
        label: 'Lesson in progress',
        url: px('8688160/pexels-photo-8688160.jpeg'),
        thumb: pxThumb('8688160/pexels-photo-8688160.jpeg'),
        credit: 'Pexels',
    },
    // ---- Kids & lessons ---------------------------------------------------
    {
        id: 'kids-goggles-underwater',
        category: 'kids',
        label: 'Young swimmer underwater',
        url: px('61129/pexels-photo-61129.jpeg'),
        thumb: pxThumb('61129/pexels-photo-61129.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'kids-coach-teaching',
        category: 'kids',
        label: 'Coach teaching child',
        url: 'https://images.unsplash.com/photo-1612701767589-1e74f1d9817e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
        thumb: 'https://images.unsplash.com/photo-1612701767589-1e74f1d9817e?crop=entropy&cs=srgb&fm=jpg&q=70&w=480',
        credit: 'Unsplash',
    },
    {
        id: 'kids-lesson-smiles',
        category: 'kids',
        label: 'Kids swim lesson',
        url: 'https://images.unsplash.com/photo-1559761246-24b39b287d99?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
        thumb: 'https://images.unsplash.com/photo-1559761246-24b39b287d99?crop=entropy&cs=srgb&fm=jpg&q=70&w=480',
        credit: 'Unsplash',
    },
    {
        id: 'kids-pool-play',
        category: 'kids',
        label: 'Child in the water',
        url: px('1263349/pexels-photo-1263349.jpeg'),
        thumb: pxThumb('1263349/pexels-photo-1263349.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'kids-float-lesson',
        category: 'kids',
        label: 'Learning to float',
        url: px('7502572/pexels-photo-7502572.jpeg'),
        thumb: pxThumb('7502572/pexels-photo-7502572.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'kids-instructor-support',
        category: 'kids',
        label: 'Instructor supporting swimmer',
        url: px('6046223/pexels-photo-6046223.jpeg'),
        thumb: pxThumb('6046223/pexels-photo-6046223.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'kids-water-confidence',
        category: 'kids',
        label: 'Building water confidence',
        url: px('6046226/pexels-photo-6046226.jpeg'),
        thumb: pxThumb('6046226/pexels-photo-6046226.jpeg'),
        credit: 'Pexels',
    },
    // ---- Technique --------------------------------------------------------
    {
        id: 'tech-breaststroke',
        category: 'technique',
        label: 'Stroke technique',
        url: px('1415810/pexels-photo-1415810.jpeg'),
        thumb: pxThumb('1415810/pexels-photo-1415810.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'tech-underwater-glide',
        category: 'technique',
        label: 'Underwater glide',
        url: px('2600493/pexels-photo-2600493.jpeg'),
        thumb: pxThumb('2600493/pexels-photo-2600493.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'tech-adult-training',
        category: 'technique',
        label: 'Adult training session',
        url: px('9219174/pexels-photo-9219174.jpeg'),
        thumb: pxThumb('9219174/pexels-photo-9219174.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'tech-lap-swim',
        category: 'technique',
        label: 'Lap swimming',
        url: px('4761792/pexels-photo-4761792.jpeg'),
        thumb: pxThumb('4761792/pexels-photo-4761792.jpeg'),
        credit: 'Pexels',
    },
    // ---- Pool & water ------------------------------------------------------
    {
        id: 'pool-clear-water',
        category: 'pool',
        label: 'Clear pool water',
        url: px('260598/pexels-photo-260598.jpeg'),
        thumb: pxThumb('260598/pexels-photo-260598.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'pool-sun-texture',
        category: 'pool',
        label: 'Sunlit water texture',
        url: 'https://images.unsplash.com/photo-1617714762566-e98324a1f642?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
        thumb: 'https://images.unsplash.com/photo-1617714762566-e98324a1f642?crop=entropy&cs=srgb&fm=jpg&q=70&w=480',
        credit: 'Unsplash',
    },
    {
        id: 'pool-ladder',
        category: 'pool',
        label: 'Poolside',
        url: px('342361/pexels-photo-342361.jpeg'),
        thumb: pxThumb('342361/pexels-photo-342361.jpeg'),
        credit: 'Pexels',
    },
    {
        id: 'pool-goggles-rest',
        category: 'pool',
        label: 'Pool detail',
        url: px('8940499/pexels-photo-8940499.jpeg'),
        thumb: pxThumb('8940499/pexels-photo-8940499.jpeg'),
        credit: 'Pexels',
    },
];

export function photosByCategory(categoryId) {
    if (!categoryId || categoryId === 'all') return SWIM_PHOTOS;
    return SWIM_PHOTOS.filter((p) => p.category === categoryId);
}

export function searchPhotos(query) {
    const q = (query || '').trim().toLowerCase();
    if (!q) return SWIM_PHOTOS;
    return SWIM_PHOTOS.filter((p) => `${p.label} ${p.category}`.toLowerCase().includes(q));
}
